import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Maximize2, Pause, Play, X } from "lucide-react";
import { IS_NATIVE, ytEmbedUrl, YT_MESSAGE_ORIGIN } from "@/lib/config";
import { usePlayer } from "@/components/contexts/playerStore";

/** Corner window size, 16:9 and the 9:16 variant for shorts. */
const MINI = { w: 216, h: 122 };
const MINI_SHORT = { w: 124, h: 220 };
const MINI_MARGIN = 12;

/** Past this much of a horizontal throw, letting go dismisses instead of snapping. */
const FLING_DISTANCE = 96;
const FLING_VELOCITY = 0.7; // px per ms

/** A drag has to travel this far before it stops counting as a tap. */
const TAP_SLOP = 6;

/** How much of the docked player has to leave the screen before it goes mini. */
const STICKY_HIDE = 80;
const STICKY_SHOW = 96;

/**
 * Docking is given this long to settle before the sticky rule may fire again.
 *
 * Expanding from the corner scrolls the slot back into view, and that scroll is
 * smooth - so the grace has to outlast it, or the player would be sent straight
 * back to the corner while the page was still travelling.
 */
const STICKY_GRACE = 1400;

const clamp = (value, min, max) => Math.min(Math.max(value, min), Math.max(min, max));

const YT_LISTENER_ID = "tz-global-player";

/**
 * The single video player for the whole app.
 *
 * There is one iframe here and nowhere else. It is `position: fixed` and its rect
 * is written every frame to match whatever it should be covering: the slot a page
 * or the modal registered, or the corner window. Moving it this way rather than
 * rendering it inside each surface is the entire trick - a cross-origin iframe
 * that changes parent in the DOM is torn down and reloaded by the browser, which
 * would restart the video on every navigation and defeat the point.
 *
 * Consequences worth knowing about:
 *  - Geometry is written imperatively, not through React or framer-motion, because
 *    following a scrolling slot needs per-frame updates and re-rendering the tree
 *    60 times a second to move one box is not worth it. The mode-change morph is a
 *    CSS transition switched on for the length of the animation.
 *  - The iframe stops taking pointer events while minimised or dragging. An iframe
 *    swallows everything over it, so without this the drag never reaches us and
 *    the tap goes to YouTube's own controls instead of expanding.
 */
export const GlobalVideoPlayer = () => {
  const { active, mode, surface, slot, expand, close, minimize, dock } = usePlayer();

  const shellRef = useRef(null);
  const iframeRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [dragging, setDragging] = useState(false);

  // Everything the animation frame needs, refreshed each render. The loop reads it
  // instead of closing over state, so it is never a render behind and never has to
  // be torn down and restarted just because a callback identity changed.
  const liveRef = useRef({});

  const miniPosRef = useRef(null);
  const dragRef = useRef(null);
  const dockedAtRef = useRef(0);
  const writtenRef = useRef({});
  const animTimer = useRef(null);
  const handshakeTimer = useRef(null);

  // Whether scrolling the slot back into view is allowed to re-dock on its own.
  // Disarmed when someone minimises by hand: the slot is still on screen at that
  // moment, so an armed rule would undo the button on the very next frame. It arms
  // again once the slot has actually left the viewport.
  const autoDockRef = useRef(false);

  const isShort = active?.platform === "shorts";
  const miniSize = isShort ? MINI_SHORT : MINI;

  liveRef.current = { mode, surface, slot, miniSize, minimize, dock };

  const embedUrl = useMemo(() => {
    if (!active) return null;
    const params = [
      "autoplay=1",
      "rel=0",
      "modestbranding=1",
      // Without this iOS hands playback to the fullscreen system player, which
      // throws away this element - and with it the mini player entirely.
      "playsinline=1",
      "enablejsapi=1",
      "controls=1",
      "fs=1",
    ];
    // The origin parameter has to be a real http(s) origin. In the packaged apps
    // the page is served from a custom scheme and the player API rejects that;
    // omitting it is allowed, and the postMessage calls below target "*" anyway.
    if (!IS_NATIVE) params.push(`origin=${window.location.origin}`);
    return ytEmbedUrl(active.id, params.join("&"));
  }, [active]);

  const command = useCallback((func) => {
    try {
      iframeRef.current?.contentWindow?.postMessage(
        JSON.stringify({ event: "command", func, args: [] }),
        "*"
      );
    } catch {
      /* the frame is cross-origin and may not be ready; nothing to recover */
    }
  }, []);

  const minimizeByHand = useCallback(() => {
    autoDockRef.current = false;
    minimize();
  }, [minimize]);

  const togglePlay = useCallback(() => {
    command(isPlaying ? "pauseVideo" : "playVideo");
    setIsPlaying((p) => !p);
  }, [command, isPlaying]);

  /**
   * Ask the embed to report its state.
   *
   * The iframe API only sends events to listeners that introduced themselves, and
   * the id we hand over comes back on every message - which is how the handler
   * below tells our player apart from the muted feed embeds, who are also posting.
   */
  const handshake = useCallback(() => {
    const introduce = () => {
      try {
        iframeRef.current?.contentWindow?.postMessage(
          JSON.stringify({ event: "listening", id: YT_LISTENER_ID }),
          "*"
        );
      } catch {
        /* the frame is not accepting messages yet */
      }
    };
    introduce();
    // The frame's load event fires before the player inside it is ready, and a
    // handshake sent too early is simply dropped - so it is sent again once the
    // player has had time to come up. Without this the mini player's play/pause
    // icon can sit on the wrong state for the whole video.
    clearTimeout(handshakeTimer.current);
    handshakeTimer.current = setTimeout(introduce, 1200);
    setIsPlaying(true);
  }, []);

  useEffect(() => () => clearTimeout(handshakeTimer.current), []);

  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== YT_MESSAGE_ORIGIN) return;
      let data;
      try {
        data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      } catch {
        return;
      }
      if (data?.id && data.id !== YT_LISTENER_ID) return;

      const state =
        data?.event === "onStateChange"
          ? data.info
          : data?.event === "infoDelivery"
          ? data.info?.playerState
          : undefined;
      if (typeof state !== "number") return;
      // 1 = playing, 2 = paused, 0 = ended.
      if (state === 1) setIsPlaying(true);
      else if (state === 2 || state === 0) setIsPlaying(false);
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  /** Turn the CSS transition on for one mode change, then off so scrolling tracks. */
  const animateOnce = useCallback(() => {
    const el = shellRef.current;
    if (!el) return;
    el.classList.add("tz-player-animating");
    clearTimeout(animTimer.current);
    animTimer.current = setTimeout(() => {
      shellRef.current?.classList.remove("tz-player-animating");
    }, 340);
  }, []);

  useEffect(() => () => clearTimeout(animTimer.current), []);

  useEffect(() => {
    if (mode === "closed") return;
    animateOnce();
    if (mode === "docked") dockedAtRef.current = performance.now();
  }, [mode, slot, animateOnce]);

  // Position and size, every frame, for as long as something is playing.
  useEffect(() => {
    if (mode === "closed") return undefined;

    let frame;
    const tick = () => {
      frame = requestAnimationFrame(tick);

      const el = shellRef.current;
      if (!el) return;
      // In a PiP window the stylesheet takes the player over completely; fighting
      // it from here would just be two sets of styles disagreeing every frame.
      if (document.documentElement.classList.contains("tz-pip")) return;

      const live = liveRef.current;
      let target = null;

      if (live.mode === "docked" && live.slot) {
        const rect = live.slot.getBoundingClientRect();
        // Scrolling the player off the top hands it to the corner window, the way
        // it happens on a video page while reading what is underneath. Only pages
        // do this: the modal's own scrolling would keep tearing itself down.
        if (
          live.surface === "page" &&
          rect.bottom < STICKY_HIDE &&
          performance.now() - dockedAtRef.current > STICKY_GRACE
        ) {
          autoDockRef.current = true;
          live.minimize();
          return;
        }
        target = {
          x: rect.left,
          y: rect.top,
          w: rect.width,
          h: rect.height,
          radius: Number(live.slot.dataset.tzSlotRadius) || 0,
        };
      } else if (live.mode === "mini") {
        // Re-dock the moment the slot scrolls back into view, so the corner window
        // is a consequence of where the page is rather than a mode to escape.
        if (live.surface === "page" && live.slot && !dragRef.current) {
          const rect = live.slot.getBoundingClientRect();
          if (!autoDockRef.current) {
            if (rect.bottom < STICKY_HIDE) autoDockRef.current = true;
          } else if (rect.top > STICKY_SHOW && rect.top < window.innerHeight - rect.height / 2) {
            live.dock();
            return;
          }
        }
        const { w, h } = live.miniSize;
        const bodyStyle = window.getComputedStyle(document.body);
        // env(safe-area-inset-*) is already resolved to pixels on the body in the
        // packaged app, so reading it back beats re-deriving the insets here.
        const safeTop = parseFloat(bodyStyle.paddingTop) || 0;
        const safeBottom = parseFloat(bodyStyle.paddingBottom) || 0;
        const maxX = window.innerWidth - w - MINI_MARGIN;
        const maxY = window.innerHeight - h - MINI_MARGIN - safeBottom;
        const anchor = miniPosRef.current || { x: maxX, y: maxY };
        target = {
          x: clamp(anchor.x, MINI_MARGIN, maxX),
          y: clamp(anchor.y, MINI_MARGIN + safeTop, maxY),
          w,
          h,
          radius: 12,
        };
      }

      if (!target) {
        el.style.opacity = "0";
        el.style.pointerEvents = "none";
        return;
      }

      el.style.opacity = "1";
      el.style.pointerEvents = "auto";

      const written = writtenRef.current;
      if (written.x !== target.x || written.y !== target.y) {
        el.style.transform = `translate3d(${target.x}px, ${target.y}px, 0)`;
      }
      if (written.w !== target.w) el.style.width = `${target.w}px`;
      if (written.h !== target.h) el.style.height = `${target.h}px`;
      if (written.radius !== target.radius) el.style.borderRadius = `${target.radius}px`;
      writtenRef.current = target;
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [mode]);

  const onPointerDown = useCallback(
    (event) => {
      if (mode !== "mini") return;
      const rect = shellRef.current?.getBoundingClientRect();
      if (!rect) return;
      const now = performance.now();
      dragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: rect.left,
        originY: rect.top,
        dx: 0,
        moved: false,
        lastX: event.clientX,
        lastT: now,
        velocityX: 0,
      };
      event.currentTarget.setPointerCapture?.(event.pointerId);
      setDragging(true);
    },
    [mode]
  );

  const onPointerMove = useCallback((event) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;
    if (Math.abs(dx) > TAP_SLOP || Math.abs(dy) > TAP_SLOP) drag.moved = true;

    const now = performance.now();
    const elapsed = Math.max(1, now - drag.lastT);
    drag.velocityX = (event.clientX - drag.lastX) / elapsed;
    drag.lastX = event.clientX;
    drag.lastT = now;
    drag.dx = dx;

    miniPosRef.current = { x: drag.originX + dx, y: drag.originY + dy };
  }, []);

  const onPointerUp = useCallback(
    (event) => {
      const drag = dragRef.current;
      if (!drag) return;
      dragRef.current = null;
      setDragging(false);
      try {
        event.currentTarget.releasePointerCapture?.(event.pointerId);
      } catch {
        // pointercancel can arrive after the capture is already gone
      }

      if (!drag.moved) {
        expand();
        return;
      }
      if (Math.abs(drag.dx) > FLING_DISTANCE || Math.abs(drag.velocityX) > FLING_VELOCITY) {
        close();
        return;
      }

      // Settle against whichever side it was thrown towards, so the window always
      // ends up somewhere predictable rather than loose in the middle.
      const { w } = miniSize;
      const centre = (miniPosRef.current?.x ?? 0) + w / 2;
      const left = MINI_MARGIN;
      const right = window.innerWidth - w - MINI_MARGIN;
      miniPosRef.current = {
        x: centre < window.innerWidth / 2 ? left : right,
        y: miniPosRef.current?.y ?? 0,
      };
      animateOnce();
    },
    [expand, close, miniSize, animateOnce]
  );

  if (!active || mode === "closed" || !embedUrl) return null;

  const interactive = mode === "docked" && !dragging;

  return (
    <div
      ref={shellRef}
      // The native PiP layer keys on this attribute: the stylesheet promotes it to
      // fill the window and MainActivity posts the resume command into it.
      data-tz-player
      data-testid="global-video-player"
      className="tz-player-shell fixed left-0 top-0 z-[110] overflow-hidden bg-black shadow-2xl"
      style={{ width: 0, height: 0, opacity: 0 }}
    >
      <iframe
        // The key is the video id and nothing else. Anything that changes it
        // reloads the embed, so a re-open of what is already playing must reach
        // here as the same id - see the identity check in PlayerProvider.play.
        key={active.id}
        ref={iframeRef}
        src={embedUrl}
        title={active.title}
        onLoad={handshake}
        className="w-full h-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
        allowFullScreen
        style={{ border: 0, pointerEvents: interactive ? "auto" : "none" }}
      />

      {mode === "mini" && (
        <div
          className="absolute inset-0 touch-none cursor-grab active:cursor-grabbing"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          data-testid="mini-player-surface"
        >
          <div className="absolute inset-x-0 top-0 h-9 bg-gradient-to-b from-black/70 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-black/70 to-transparent" />

          <button
            onPointerDown={(e) => e.stopPropagation()}
            onClick={close}
            className="absolute top-1 right-1 w-8 h-8 flex items-center justify-center rounded-lg text-white/90 hover:bg-white/15 transition-colors"
            aria-label="Close player"
          >
            <X className="w-4 h-4" />
          </button>

          <button
            onPointerDown={(e) => e.stopPropagation()}
            onClick={togglePlay}
            className="absolute bottom-1 left-1 w-8 h-8 flex items-center justify-center rounded-lg text-white/90 hover:bg-white/15 transition-colors"
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>

          <button
            onPointerDown={(e) => e.stopPropagation()}
            onClick={expand}
            className="absolute bottom-1 right-1 w-8 h-8 flex items-center justify-center rounded-lg text-white/90 hover:bg-white/15 transition-colors"
            aria-label="Expand player"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
        </div>
      )}

      {mode === "docked" && surface === "page" && (
        <button
          onClick={minimizeByHand}
          className="absolute top-2 left-2 w-9 h-9 flex items-center justify-center rounded-lg bg-black/55 text-white/90 hover:bg-black/75 backdrop-blur-sm transition-colors"
          aria-label="Minimise player"
        >
          {/* Reusing the expand glyph rotated would read as "fullscreen"; a plain
              inward chevron pair is the closest thing to "shrink to corner". */}
          <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 14h6v6M20 10h-6V4" />
          </svg>
        </button>
      )}
    </div>
  );
};
